import { AlertCircle, Calendar, ChevronRight, Clock, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  categoryTone,
  eventMatchesDateTime,
  eventMatchesFilter,
  eventMatchesSearch,
  groupEventsByDate,
  severityTone,
  sortEvents,
  timelineFilters,
  type TimelineFilter,
} from "../../../lib/events";
import { formatDateHeading, formatTime } from "../../../lib/baseline/format";
import type { ParsedEvent, Upload } from "../../../types/domain";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { getDefaultTimelineRange, TimelineLens, type TimelineRange } from "./TimelineLens";

export function EventTimeline({
  events,
  upload,
  eventLinkBase,
}: {
  events: ParsedEvent[];
  upload?: Upload;
  eventLinkBase?: string;
}) {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [filter, setFilter] = useState<TimelineFilter>((params.get("filter") as TimelineFilter) || "ALL");
  const [sort, setSort] = useState<"newest" | "oldest">(
    params.get("sort") === "oldest" ? "oldest" : "newest",
  );
  const [dateFilter, setDateFilter] = useState(params.get("date") ?? "");
  const [timeFilter, setTimeFilter] = useState(params.get("time") ?? "");
  const defaultTimelineRange = useMemo(() => getDefaultTimelineRange(events), [events]);
  const [rangeStart, setRangeStart] = useState(params.get("from") ?? defaultTimelineRange?.start ?? "");
  const [rangeEnd, setRangeEnd] = useState(params.get("to") ?? defaultTimelineRange?.end ?? "");
  const [visibleCount, setVisibleCount] = useState(120);
  const hasDateTimeFilter = Boolean(dateFilter || timeFilter || rangeStart || rangeEnd);

  useEffect(() => {
    if (params.has("from") || params.has("to") || rangeStart || !defaultTimelineRange) return;
    setRangeStart(defaultTimelineRange.start);
    setRangeEnd(defaultTimelineRange.end);
  }, [defaultTimelineRange, params, rangeStart]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    query ? next.set("q", query) : next.delete("q");
    filter !== "ALL" ? next.set("filter", filter) : next.delete("filter");
    sort !== "newest" ? next.set("sort", sort) : next.delete("sort");
    dateFilter ? next.set("date", dateFilter) : next.delete("date");
    timeFilter ? next.set("time", timeFilter) : next.delete("time");
    rangeStart ? next.set("from", rangeStart) : next.delete("from");
    rangeEnd ? next.set("to", rangeEnd) : next.delete("to");
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
  }, [dateFilter, filter, params, query, rangeEnd, rangeStart, setParams, sort, timeFilter]);

  const filtered = useMemo(() => {
    const matching = events.filter(
      (event) =>
        eventMatchesFilter(event, filter) &&
        eventMatchesSearch(event, upload, query) &&
        eventMatchesDateTime(event, dateFilter, timeFilter) &&
        eventMatchesTimeRange(event, rangeStart, rangeEnd),
    );
    return sortEvents(matching, sort);
  }, [dateFilter, events, filter, query, rangeEnd, rangeStart, sort, timeFilter, upload]);
  const visible = filtered.slice(0, visibleCount);
  const groups = groupEventsByDate(visible);

  function selectTimelineRange(range: TimelineRange) {
    setRangeStart(range.start);
    setRangeEnd(range.end);
    setVisibleCount(120);
  }

  function clearTimeFilters() {
    setDateFilter("");
    setTimeFilter("");
    setRangeStart("");
    setRangeEnd("");
    setVisibleCount(120);
  }

  return (
    <div className="space-y-4">
      <TimelineLens
        activeFilter={filter}
        events={events}
        rangeEnd={rangeEnd}
        rangeStart={rangeStart}
        upload={upload}
        onClearRange={clearTimeFilters}
        onFilterChange={(nextFilter) => {
          setFilter(nextFilter);
          setVisibleCount(120);
        }}
        onRangeChange={selectTimelineRange}
      />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(16rem,1fr)_minmax(10rem,12rem)_minmax(8rem,10rem)_auto_auto]">
        <label className="relative block md:col-span-2 xl:col-span-1">
          <span className="sr-only">Search events</span>
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" aria-hidden />
          <Input
            className="pl-9"
            placeholder="Search title, summary, raw code, zone, program, error text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setVisibleCount(120);
            }}
          />
        </label>
        <label className="relative block">
          <span className="sr-only">Filter by date</span>
          <Calendar className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" aria-hidden />
          <Input
            aria-label="Filter by date"
            className="pl-9"
            max={upload?.dateRangeEnd?.slice(0, 10)}
            min={upload?.dateRangeStart?.slice(0, 10)}
            type="date"
            value={dateFilter}
            onChange={(event) => {
              setDateFilter(event.target.value);
              setVisibleCount(120);
            }}
          />
        </label>
        <label className="relative block">
          <span className="sr-only">Filter by time</span>
          <Clock className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" aria-hidden />
          <Input
            aria-label="Filter by time"
            className="pl-9"
            step={60}
            type="time"
            value={timeFilter}
            onChange={(event) => {
              setTimeFilter(event.target.value);
              setVisibleCount(120);
            }}
          />
        </label>
        <select
          aria-label="Sort events"
          className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm"
          value={sort}
          onChange={(event) => setSort(event.target.value as "newest" | "oldest")}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        {hasDateTimeFilter ? (
          <Button
            aria-label="Clear date and time"
            className="h-11 w-full md:w-11"
            size="icon"
            title="Clear date and time"
            type="button"
            variant="secondary"
            onClick={clearTimeFilters}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Timeline filters">
        {timelineFilters.map((item) => (
          <button
            key={item.id}
            className={`min-h-10 whitespace-nowrap rounded-full px-3 text-sm font-semibold ring-1 ring-inset ${
              filter === item.id
                ? "bg-navy-900 text-white ring-navy-900"
                : "bg-white text-slate-700 ring-slate-200"
            }`}
            onClick={() => {
              setFilter(item.id);
              setVisibleCount(120);
            }}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-slate-400" aria-hidden />
          <p className="mt-3 font-semibold text-navy-900">No events match this search.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([date, dateEvents]) => (
            <section key={date} className="space-y-3">
              <h3 className="sticky top-16 z-10 rounded-md bg-slate-50 py-2 text-sm font-bold text-navy-900">
                {formatDateHeading(`${date}T00:00:00`)}
              </h3>
              <div className="timeline-line space-y-3 pl-8">
                {dateEvents.map((event) => (
                  <EventCard key={event.eventId} event={event} linkBase={eventLinkBase} />
                ))}
              </div>
            </section>
          ))}
          {filtered.length > visible.length ? (
            <div className="flex justify-center">
              <Button variant="secondary" onClick={() => setVisibleCount((count) => count + 120)}>
                Load more events
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function eventMatchesTimeRange(event: ParsedEvent, rangeStart: string, rangeEnd: string) {
  return (!rangeStart || event.timestamp >= rangeStart) && (!rangeEnd || event.timestamp <= rangeEnd);
}

function EventCard({ event, linkBase }: { event: ParsedEvent; linkBase?: string }) {
  const content = (
    <article className="relative rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <span className="absolute -left-[26px] top-5 h-3 w-3 rounded-full border-2 border-white bg-navy-900" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase text-slate-500">{formatTime(event.timestamp)}</p>
          <h4 className="mt-1 text-base font-bold text-navy-950">{event.title}</h4>
        </div>
        {linkBase ? <ChevronRight className="mt-1 h-5 w-5 flex-none text-slate-400" aria-hidden /> : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Badge className={categoryTone(event.category)}>{event.category.replace("_", " ")}</Badge>
        <Badge className={severityTone(event.severity)}>{event.severity}</Badge>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-700">{event.summary}</p>
    </article>
  );

  if (!linkBase) return content;
  return (
    <Link className="block hover:opacity-95" to={`${linkBase}/${event.eventId}`}>
      {content}
    </Link>
  );
}
